import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  normalizeSourceTypeActionsData,
  registerClaimIntakeStoreRef,
  resolveClaimIntakeBearerToken,
  useGetAiWorkersByCategoryQuery,
  useGetAiWorkersBySourceTypeQuery,
  useTypedSelector,
  type RootState,
} from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import CtySpinner from '../spinner/CtySpinner';
import {
  STEPS,
  StepperHeader,
  getClaimRequests,
  makeInitialClaimRepair,
  mapClaimRequestsToRepairEntries,
  mergeMappedRepairsWithUserEdits,
  resetClaimIntakeLineIdSeq,
  resolveActionSourceId,
  type ClaimGenerationReviewData,
  type ClaimRepairEntry,
  type ContractData,
} from './claimIntakeUtils';
import { ClaimScoreSidebar } from './ClaimScoreSidebar';
import { ClaimIntakeHeaderFeedback } from './ClaimIntakeHeaderFeedback';
import { ContractLookupTab } from './ContractLookupTab';
import { hasBlockingPendingClaims } from './claimIntakeAgentApi';
import {
  ExistingClaimIntakePanel,
  type ExistingClaimPhase,
} from './ExistingClaimIntakePanel';
import { VehicleClaimsTab } from './VehicleClaimsTab';
import { RepairsDocsTab } from './RepairsDocsTab';
import { ReviewTab } from './ReviewTab';
import type { ClaimIntakeParserSetters } from './claimIntakeParser';
import {
  buildCustomerAddressFromContract,
  applyCustomerFieldsFromContract,
  normalizeServicerClassification,
  type ClaimCustomerData,
} from './buildUnifiedClaimPayload';
import { INITIAL_VEHICLE_NOTES, type VehicleNotesFormState } from './vehicleNotesUtils';
import type { AdditionalSourceItem } from './additionalInformationUpload';
import {
  CLAIM_INTAKE_AGENT_NAME,
  getClaimIntakeAgentsStreamBaseUrl,
  initClaimIntakeSession,
  type ClaimIntakeSession,
} from './claimIntakeAgentApi';
import { buildClaimIntakePrerequisiteSnapshot } from './claimIntakeRum';
import { recordRumPrerequisiteCheck } from '../rum/rumOperationTracker';
import type { PreviousClaimsFetchConfig } from './claimIntakeReadOnlySections';
import { INITIAL_PREVIOUS_CLAIMS_STATE, type PreviousClaimsState } from './previousClaimsApi';
import { ClaimIntakeVoiceFill } from './ClaimIntakeVoiceFill';
import type { ClaimIntakeVoiceFormSetters } from './applyVoiceAgentParsedToClaimIntake';
import { ClaimIntakePdfPreviewPanel } from './ClaimIntakePdfPreviewPanel';
import { getPdfFiles, isPdfFile } from './claimIntakeDocumentView';
import type { ContractLookupTabHandle } from './ContractLookupTab';

const LEFT_W = { def: 240, min: 200, max: 300 } as const;
const RIGHT_W = { def: 400, min: 300, max: 550 } as const;
const CENTER_MIN = 220;

function clampW(px: number, total: number, bounds: { min: number; max: number }, otherW: number) {
  const hi = Math.min(bounds.max, Math.max(bounds.min, total - otherW - CENTER_MIN - 4));
  return Math.round(Math.max(bounds.min, Math.min(hi, px)));
}

const SEPARATOR_CLASS =
  'm-0 p-0 h-full min-h-0 self-stretch border-y-0 border-x border-transparent w-1 shrink-0 cursor-col-resize select-none touch-none bg-default-100 hover:bg-primary/25 hover:border-primary/20';

/** Tailwind `lg` breakpoint; both score sidebars stay mounted — only one may call start-stream / dify_stream. */
function useIsMinWidthLg(): boolean {
  const [matches, setMatches] = useState(() =>
    typeof globalThis.window === 'object'
      ? globalThis.window.matchMedia('(min-width: 1024px)').matches
      : false
  );
  useEffect(() => {
    if (typeof globalThis.window !== 'object') return;
    const mq = globalThis.window.matchMedia('(min-width: 1024px)');
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return matches;
}

export type ClaimIntakePageProps = Readonly<{
  /** When true, shows the voice-agent form-fill control in the claim intake header. */
  isVoiceAgentAccessible?: boolean;
}>;

type ClaimIntakeBootstrapStatus = 'idle' | 'loading' | 'ready' | 'error';

export default function ClaimIntakePage({
  isVoiceAgentAccessible = false,
}: ClaimIntakePageProps = {}) {
  const isLgViewport = useIsMinWidthLg();
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const authToken = useTypedSelector((state) => resolveClaimIntakeBearerToken(state));
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [contract, setContract] = useState<ContractData | null>(null);
  const [pendingClaimNumbers, setPendingClaimNumbers] = useState<string[]>([]);
  /** Ref keeps the latest contract synchronously available in callbacks that fire before React re-renders. */
  const contractRef = useRef<ContractData | null>(null);
  const [lookupVin, setLookupVin] = useState('');
  const [lookupContractNumber, setLookupContractNumber] = useState('');
  const [arrivalMethod, setArrivalMethod] = useState('');
  const [atServiceCenter, setAtServiceCenter] = useState('');
  /** Gate on Contract Lookup — must be yes before upload or VIN/contract lookup. */
  const [isServicer, setIsServicer] = useState('');
  const [vehicleNotes, setVehicleNotes] = useState<VehicleNotesFormState>(INITIAL_VEHICLE_NOTES);

  const handleVehicleNotesChange = useCallback((patch: Partial<VehicleNotesFormState>) => {
    setVehicleNotes((prev) => ({ ...prev, ...patch }));
  }, []);

  const [additionalInfoFiles, setAdditionalInfoFiles] = useState<File[]>([]);
  const [additionalSources, setAdditionalSources] = useState<AdditionalSourceItem[]>([]);
  /** Primary document upload sources — root `sources` on the universal claim payload. */
  const [documentFileSources, setDocumentFileSources] = useState<AdditionalSourceItem[]>([]);

  const [step3Reimbursement, setStep3Reimbursement] = useState('');
  const [step3PaymentEmail, setStep3PaymentEmail] = useState('');
  const [step3UploadedFiles, setStep3UploadedFiles] = useState<File[]>([]);
  const [step3ServicerName, setStep3ServicerName] = useState('');
  const [step3ContactEmail, setStep3ContactEmail] = useState('');
  const [step3ServicerContactName, setStep3ServicerContactName] = useState('');
  const [step3ServicerMobile, setStep3ServicerMobile] = useState('');
  const [step3WantText, setStep3WantText] = useState('no');
  const [step3LaborRate, setStep3LaborRate] = useState('');
  const [step3MileageIn, setStep3MileageIn] = useState('');
  const [step3InvoiceNumber, setStep3InvoiceNumber] = useState('');
  const [step3RepairOrderDate, setStep3RepairOrderDate] = useState('');
  const [step3VehicleVin, setStep3VehicleVin] = useState('');
  const [step3CustomerName, setStep3CustomerName] = useState('');
  const [step3CustomerPhone, setStep3CustomerPhone] = useState('');
  /** Customer address — pre-filled from contract, user-editable. */
  const [step3CustomerAddress1, setStep3CustomerAddress1] = useState('');
  const [step3CustomerAddress2, setStep3CustomerAddress2] = useState('');
  const [step3CustomerCity, setStep3CustomerCity] = useState('');
  const [step3CustomerState, setStep3CustomerState] = useState('');
  const [step3CustomerPostalCode, setStep3CustomerPostalCode] = useState('');
  const [step3CustomerCountryCode, setStep3CustomerCountryCode] = useState('');
  /** Servicer phone — populated from 3csmapper API response, user-editable. */
  const [step3ServicerPhone, setStep3ServicerPhone] = useState('');
  /** Servicer address — populated from 3csmapper API response, user-editable. */
  const [step3ServicerAddress1, setStep3ServicerAddress1] = useState('');
  const [step3ServicerAddress2, setStep3ServicerAddress2] = useState('');
  const [step3ServicerCity, setStep3ServicerCity] = useState('');
  const [step3ServicerState, setStep3ServicerState] = useState('');
  const [step3ServicerPostalCode, setStep3ServicerPostalCode] = useState('');
  const [step3ServicerCountryCode, setStep3ServicerCountryCode] = useState('');
  const [step3ServicerClassification, setStep3ServicerClassification] = useState('');
  const [step3ContractNumber, setStep3ContractNumber] = useState('');
  const [step3DealerNumber, setStep3DealerNumber] = useState('');
  const [step3ProductType, setStep3ProductType] = useState('');
  const [step3Repairs, setStep3Repairs] = useState<ClaimRepairEntry[]>([makeInitialClaimRepair(1)]);
  const [claimGenerationReview, setClaimGenerationReview] = useState<ClaimGenerationReviewData | null>(null);
  const [preparedClaimRequestKey, setPreparedClaimRequestKey] = useState<string | null>(null);
  /** Parsed invoice doc row last continued from Repairs — rehydrates Repairs when user returns from Review. */
  const [step3ParsedDocument, setStep3ParsedDocument] = useState<Record<string, unknown> | null>(null);
  /** Latest unified payload for score API while on Repairs & Docs (part + labor lines present). */
  const [claimScoreEnrichedForm, setClaimScoreEnrichedForm] = useState<Record<string, unknown> | null>(null);
  const [isClaimScoreLoading, setIsClaimScoreLoading] = useState(false);
  const [dummyClaimId, setDummyClaimId] = useState<string | null>(null);
  const [paymentAuthorizationAcknowledged, setPaymentAuthorizationAcknowledged] = useState(false);
  /** Shared aiworker_usage_id — sourced from estimateparser (PDF path) or VIN (manual path). */
  const [aiworkerUsageId, setAiworkerUsageId] = useState<string | null>(null);
  /** After VIN lookup runs, hide "I don't have estimate" for this session. */
  const [hideNoEstimatePrompt, setHideNoEstimatePrompt] = useState(false);
  /** New-claim wizard vs existing-claim supporting-docs flow (BRD). */
  const [intakeMode, setIntakeMode] = useState<'new' | 'existing'>('new');
  const [existingClaimPhase, setExistingClaimPhase] = useState<ExistingClaimPhase>('identify');
  /** Customer data returned by 3csmapper — shown in UI for optional editing before submission. */
  const [customerFromApi, setCustomerFromApi] = useState<ClaimCustomerData | null>(null);

  const [previousClaims, setPreviousClaims] = useState<PreviousClaimsState>(INITIAL_PREVIOUS_CLAIMS_STATE);

  const contractLookupRef = useRef<ContractLookupTabHandle | null>(null);
  const [voiceSubmitNonce, setVoiceSubmitNonce] = useState(0);

  const handleVoiceGoToStep = useCallback((step: number) => {
    setCurrentStep(step);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      for (let i = 0; i < step; i += 1) next.add(i);
      return [...next];
    });
  }, []);

  const handleVoiceSubmitClaim = useCallback(() => {
    setCurrentStep(3);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      for (let i = 0; i < 3; i += 1) next.add(i);
      return [...next];
    });
    setVoiceSubmitNonce((n) => n + 1);
  }, []);

  const [leftColW, setLeftColW] = useState<number>(LEFT_W.def);
  const [rightColW, setRightColW] = useState<number>(RIGHT_W.def);
  const layoutRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const colDragRef = useRef<null | { edge: 'left' | 'right'; startX: number; startLeft: number; startRight: number }>(
    null
  );
  const { isAuthenticated } = useSelector((state: RootState) => state.auth.user);
  /** Right score panel — logged-in users only (hidden for guest / PAL public access). */
  const showClaimScoreSidebar = isAuthenticated;
  const showPreviousClaims = isAuthenticated;

  const handleLookupStart = useCallback(() => {
    setContract(null);
    contractRef.current = null;
    setClaimScoreEnrichedForm(null);
    setIsClaimScoreLoading(false);
    setDummyClaimId(null);
    setPreviousClaims(INITIAL_PREVIOUS_CLAIMS_STATE);
  }, []);

  const handleContractFound = useCallback((c: ContractData) => {
    contractRef.current = c;
    setContract(c);
    applyCustomerFieldsFromContract(c, {
      onCustomerNameChange: setStep3CustomerName,
      onCustomerPhoneChange: setStep3CustomerPhone,
      onCustomerAddress1Change: setStep3CustomerAddress1,
      onCustomerAddress2Change: setStep3CustomerAddress2,
      onCustomerCityChange: setStep3CustomerCity,
      onCustomerStateChange: setStep3CustomerState,
      onCustomerPostalCodeChange: setStep3CustomerPostalCode,
      onCustomerCountryCodeChange: setStep3CustomerCountryCode,
    });
    setClaimScoreEnrichedForm(null);
    setDummyClaimId(null);
    setCompletedSteps((prev) => (prev.includes(0) ? prev : [...prev, 0]));
  }, []);

  useEffect(() => {
    if (!contract) {
      setStep3ContractNumber('');
      setStep3DealerNumber('');
      setStep3ProductType('');
      return;
    }
    setStep3ContractNumber((prev) => (prev.trim() ? prev : contract.contractNumber ?? ''));
    const pt = contract.coverage?.productType?.code ?? contract.coverage?.productType?.description ?? '';
    setStep3ProductType((prev) => (prev.trim() ? prev : pt));
  }, [contract]);

  useEffect(() => {
    if (!contract?.vehicle?.vin?.trim()) return;
    setStep3VehicleVin((prev) => (prev.trim() ? prev : contract.vehicle.vin));
  }, [contract]);

  /** Rehydrate servicer dealer_no from generated claim when returning from Review. */
  useEffect(() => {
    const servicer = claimGenerationReview?.claimObject?.servicer;
    if (servicer == null || typeof servicer !== 'object' || Array.isArray(servicer)) return;
    const dealerNo = (servicer as Record<string, unknown>).dealer_no;
    if (typeof dealerNo === 'string' && dealerNo.trim()) {
      setStep3DealerNumber((prev) => (prev.trim() ? prev : dealerNo));
    }
  }, [claimGenerationReview?.claimObject]);

  /** Rehydrate servicer dealer_no from generated claim when returning from Review. */

  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewSelectedFile, setPdfPreviewSelectedFile] = useState<File | null>(null);
  const [pdfPanelWidth, setPdfPanelWidth] = useState<number | null>(null);

  const openPdfPreview = useCallback(
    (file?: File | null) => {
      const pdfs = getPdfFiles(step3UploadedFiles);
      if (pdfs.length === 0) return;
      const target = file && isPdfFile(file) ? file : pdfs[0];
      setPdfPreviewSelectedFile(target);
      setPdfPreviewOpen(true);
      if (pdfPanelWidth === null && layoutRef.current) {
        setPdfPanelWidth(Math.round(layoutRef.current.getBoundingClientRect().width * 0.5));
      }
    },
    [step3UploadedFiles, pdfPanelWidth]
  );

  const closePdfPreview = useCallback(() => {
    setPdfPreviewOpen(false);
  }, []);

  useEffect(() => {
    if (!pdfPreviewOpen) return;
    if (pdfPanelWidth !== null) return;
    const el = layoutRef.current;
    if (!el) return;
    setPdfPanelWidth(Math.round(el.getBoundingClientRect().width * 0.5));
  }, [pdfPreviewOpen, pdfPanelWidth]);

  const pdfFiles = useMemo(() => getPdfFiles(step3UploadedFiles), [step3UploadedFiles]);


  useEffect(() => {
    if (pdfFiles.length === 0) {
      setPdfPreviewOpen(false);
      setPdfPreviewSelectedFile(null);
      return;
    }
    if (
      pdfPreviewSelectedFile &&
      pdfFiles.some(
        (f) =>
          f.name === pdfPreviewSelectedFile.name &&
          f.lastModified === pdfPreviewSelectedFile.lastModified &&
          f.size === pdfPreviewSelectedFile.size
      )
    ) {
      return;
    }
    setPdfPreviewSelectedFile(pdfFiles[0]);
  }, [pdfFiles, pdfPreviewSelectedFile]);


  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0 });
  }, [currentStep]);

  const stepLabel = STEPS[currentStep].label;
  const pageTitle = (() => {
    if (intakeMode === 'existing') {
      return 'Submit Information on an Existing Claim Here';
    }
    if (currentStep === 0) return 'Start a New Claim Here';
    return stepLabel;
  })();
  const showStepCounter = intakeMode === 'new';

  const onColHandlePointerDown = useCallback(
    (edge: 'left' | 'right') => (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      colDragRef.current = {
        edge,
        startX: e.clientX,
        startLeft: leftColW,
        startRight: rightColW,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [leftColW, rightColW]
  );

  const onColHandlePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const d = colDragRef.current;
    if (!d) return;
    const el = layoutRef.current;
    if (!el) return;
    const total = el.getBoundingClientRect().width;
    if (d.edge === 'left') {
      setLeftColW(clampW(d.startLeft + (e.clientX - d.startX), total, LEFT_W, d.startRight));
    } else {
      setRightColW(clampW(d.startRight - (e.clientX - d.startX), total, RIGHT_W, d.startLeft));
    }
  }, []);

  const onColHandlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    if (colDragRef.current) {
      colDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    }
  }, []);

  useEffect(() => {
    registerClaimIntakeStoreRef({ getState: store.getState, dispatch });
    return () => registerClaimIntakeStoreRef(null);
  }, [store, dispatch]);

  const tokenReady = Boolean(authToken?.trim());

  const {
    data: agentWorkers,
    isLoading: agentsLoading,
    isFetching: agentsFetching,
    isError: agentsError,
    isSuccess: agentsSuccess,
  } = useGetAiWorkersByCategoryQuery(
    { category: 'agent' as any },
    { skip: !tokenReady, refetchOnMountOrArgChange: true }
  );

  const {
    data: actionsListRaw,
    isLoading: actionsLoading,
    isFetching: actionsFetching,
    isError: actionsError,
    isSuccess: actionsSuccess,
  } = useGetAiWorkersBySourceTypeQuery('action', {
    skip: !tokenReady,
    refetchOnMountOrArgChange: true,
  });

  const actionSources = useMemo(
    () => normalizeSourceTypeActionsData(actionsListRaw),
    [actionsListRaw]
  );

  const claimIntakeAiworkerId: string =
    (agentWorkers as any)?.agents?.find(
      (w: any) => typeof w.name === 'string' && w.name.trim().toLowerCase() === CLAIM_INTAKE_AGENT_NAME
    )?.aiworker_id ??
    actionSources.find(
      (w: any) => typeof w.name === 'string' && w.name.trim().toLowerCase() === CLAIM_INTAKE_AGENT_NAME
    )?.aiworker_id ??
    '';

  const claimIntakeAgentsBaseUrl = getClaimIntakeAgentsStreamBaseUrl();

  const lookupSourceId: string = resolveActionSourceId(actionSources, {
    exactNames: ['Contract & VIN Reader'],
    nameIncludes: 'contract & vin reader',
  });
  const submitSourceId: string = resolveActionSourceId(actionSources, {
    exactNames: ['Create Claim'],
    nameIncludes: 'create claim',
  });
  const previousClaimsSourceId: string = resolveActionSourceId(actionSources, {
    exactNames: ['pcrs claims by vin/contract/claims'],
    nameIncludes: 'pcrs claims by vin/contract/claims',
  });
  const servicerDetailsSourceId: string = resolveActionSourceId(actionSources, {
    exactNames: ['Get Servicer Details'],
    nameIncludes: 'get servicer details',
  });
  const dealerDetailsSourceId: string = resolveActionSourceId(actionSources, {
    exactNames: ['Get Dealer Details'],
    nameIncludes: 'get dealer details',
  });

  const [claimIntakeSession, setClaimIntakeSession] = useState<ClaimIntakeSession | null>(null);
  const [bootstrapStatus, setBootstrapStatus] = useState<ClaimIntakeBootstrapStatus>('idle');
  const sessionInitRef = useRef(false);

  const depsQueryLoading =
    !tokenReady ||
    agentsLoading ||
    agentsFetching ||
    actionsLoading ||
    actionsFetching;

  const depsQuerySettled = tokenReady && agentsSuccess && actionsSuccess;
  const depsQueryFailed = agentsError || actionsError;

  useEffect(() => {
    if (!depsQuerySettled) return;

    if (!claimIntakeAiworkerId) {
      setBootstrapStatus('error');
      return;
    }

    if (sessionInitRef.current) return;

    let cancelled = false;
    sessionInitRef.current = true;
    setBootstrapStatus('loading');

    void initClaimIntakeSession({
      aiworkerId: claimIntakeAiworkerId,
      token: authToken!,
      baseUrl: claimIntakeAgentsBaseUrl,
    })
      .then((session) => {
        if (cancelled) return;
        setClaimIntakeSession(session);
        setBootstrapStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          sessionInitRef.current = false;
          setBootstrapStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [depsQuerySettled, claimIntakeAiworkerId, authToken, claimIntakeAgentsBaseUrl]);

  useEffect(() => {
    if (!depsQuerySettled) return;
    const snapshot = buildClaimIntakePrerequisiteSnapshot({
      tokenReady,
      agentsReady: agentsSuccess,
      actionsReady: actionsSuccess,
      claimIntakeAiworkerId,
      lookupSourceId,
      submitSourceId,
      sessionReady: bootstrapStatus === 'ready' && Boolean(claimIntakeSession?.sessionId),
    });
    recordRumPrerequisiteCheck('claim_intake', 'bootstrap', {
      ready: snapshot.ready,
      missing: snapshot.missing,
      bootstrap_status: bootstrapStatus,
      has_session: Boolean(claimIntakeSession?.sessionId),
      aiworker_id: claimIntakeAiworkerId || null,
    });
  }, [
    depsQuerySettled,
    tokenReady,
    agentsSuccess,
    actionsSuccess,
    claimIntakeAiworkerId,
    lookupSourceId,
    submitSourceId,
    bootstrapStatus,
    claimIntakeSession?.sessionId,
  ]);

  const showBootstrapError =
    depsQueryFailed ||
    (depsQuerySettled && !claimIntakeAiworkerId) ||
    bootstrapStatus === 'error';
  const showBootstrapLoader =
    !showBootstrapError &&
    (depsQueryLoading || bootstrapStatus === 'loading' || bootstrapStatus === 'idle');

  const previousClaimsFetchConfig = useMemo((): PreviousClaimsFetchConfig | null => {
    const contractNumber = contract?.contractNumber?.trim();
    if (!contractNumber) return null;
    return {
      contractNumber,
      session: claimIntakeSession,
      token: authToken,
      sourceId: previousClaimsSourceId,
      agentsBaseUrl: claimIntakeAgentsBaseUrl,
      aiworkerUsageId,
    };
  }, [
    contract?.contractNumber,
    claimIntakeSession,
    authToken,
    previousClaimsSourceId,
    claimIntakeAgentsBaseUrl,
    aiworkerUsageId,
  ]);

  const clearStep3RepairArtifacts = useCallback(() => {
    setStep3ParsedDocument(null);
    setClaimGenerationReview(null);
    setPreparedClaimRequestKey(null);
    setClaimScoreEnrichedForm(null);
    setAiworkerUsageId(null);
    setHideNoEstimatePrompt(false);
    setDocumentFileSources([]);
    setCustomerFromApi(null);
    resetClaimIntakeLineIdSeq();
    setStep3MileageIn('');
    setStep3InvoiceNumber('');
    setStep3RepairOrderDate('');
    setStep3VehicleVin(contract?.vehicle?.vin ?? '');
    setStep3CustomerName('');
    setStep3CustomerPhone('');
    // Reset customer address back to contract data
    const cAddr = contract ? buildCustomerAddressFromContract(contract) : null;
    setStep3CustomerAddress1(cAddr?.address1 ?? '');
    setStep3CustomerAddress2(cAddr?.address2 ?? '');
    setStep3CustomerCity(cAddr?.city ?? '');
    setStep3CustomerState(cAddr?.state ?? '');
    setStep3CustomerPostalCode(cAddr?.postal_code ?? '');
    setStep3CustomerCountryCode(cAddr?.country_code ?? '');
    // Reset servicer details (re-populated from next 3csmapper call)
    setStep3ServicerPhone('');
    setStep3ServicerAddress1('');
    setStep3ServicerAddress2('');
    setStep3ServicerCity('');
    setStep3ServicerState('');
    setStep3ServicerPostalCode('');
    setStep3ServicerCountryCode('');
    setStep3ServicerClassification('');
    setStep3ContractNumber(contract?.contractNumber ?? '');
    setStep3DealerNumber('');
    setStep3ProductType(
      contract?.coverage?.productType?.code ?? contract?.coverage?.productType?.description ?? ''
    );
    setStep3Repairs([makeInitialClaimRepair(1)]);
  }, [contract]);

  /** Clears prepared 3csmapper output so Prepare Claim can run again after a re-parse. */
  const resetClaimGeneration = useCallback(() => {
    setClaimGenerationReview(null);
    setPreparedClaimRequestKey(null);
    setClaimScoreEnrichedForm(null);
  }, []);

  const handleClaimObjectUpdated = useCallback((claimObject: Record<string, unknown>) => {
    setClaimGenerationReview((prev) => (prev ? { ...prev, claimObject } : null));
    const requests = getClaimRequests(claimObject);
    if (requests.length > 0) {
      setStep3Repairs((previous) =>
        mergeMappedRepairsWithUserEdits(previous, mapClaimRequestsToRepairEntries(requests))
      );
    }
  }, []);

  /** Stable setter map applied by ContractLookupTab whenever a repair document is parsed in step 1. */
  const contractLookupFormSetters = useMemo<ClaimIntakeParserSetters>(
    () => ({
      onServicerNameChange: setStep3ServicerName,
      onServicerPhoneChange: setStep3ServicerPhone,
      onServicerAddress1Change: setStep3ServicerAddress1,
      onServicerAddress2Change: setStep3ServicerAddress2,
      onServicerCityChange: setStep3ServicerCity,
      onServicerStateChange: setStep3ServicerState,
      onServicerPostalCodeChange: setStep3ServicerPostalCode,
      onServicerCountryCodeChange: setStep3ServicerCountryCode,
      onLaborRateChange: setStep3LaborRate,
      onContactEmailChange: setStep3ContactEmail,
      onRepairsChange: setStep3Repairs,
      onMileageInChange: setStep3MileageIn,
      onInvoiceNumberChange: setStep3InvoiceNumber,
      onRepairOrderDateChange: setStep3RepairOrderDate,
      onCustomerNameChange: setStep3CustomerName,
      onCustomerPhoneChange: setStep3CustomerPhone,
      onCustomerAddress1Change: setStep3CustomerAddress1,
      onCustomerAddress2Change: setStep3CustomerAddress2,
      onCustomerCityChange: setStep3CustomerCity,
      onCustomerStateChange: setStep3CustomerState,
      onCustomerPostalCodeChange: setStep3CustomerPostalCode,
      onCustomerCountryCodeChange: setStep3CustomerCountryCode,
      onVehicleVinForClaimChange: setStep3VehicleVin,
      onContractNumberChange: setLookupContractNumber,
    }),
    []
  );

  const voiceFormSetters = useMemo<ClaimIntakeVoiceFormSetters>(
    () => ({
      setLookupVin,
      setLookupContractNumber,
      setArrivalMethod,
      setAtServiceCenter,
      setStep3Reimbursement,
      setStep3PaymentEmail,
      setStep3ContractNumber,
      setStep3MileageIn,
      setStep3InvoiceNumber,
      setStep3RepairOrderDate,
      setStep3VehicleVin,
      setStep3CustomerName,
      setStep3CustomerPhone,
      setStep3CustomerAddress1,
      setStep3CustomerAddress2,
      setStep3CustomerCity,
      setStep3CustomerState,
      setStep3CustomerPostalCode,
      setStep3CustomerCountryCode,
      setStep3ServicerName,
      setStep3ServicerPhone,
      setStep3ContactEmail,
      setStep3ServicerContactName,
      setStep3ServicerMobile,
      setStep3WantText,
      setStep3LaborRate,
      setStep3ServicerAddress1,
      setStep3ServicerAddress2,
      setStep3ServicerCity,
      setStep3ServicerState,
      setStep3ServicerPostalCode,
      setStep3ServicerCountryCode,
      setStep3DealerNumber,
      setStep3Repairs,
      setVehicleNotes,
    }),
    []
  );

  if (showBootstrapError) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 bg-default-100 px-6 text-center">
        <Icon icon="mdi:alert-circle-outline" className="text-3xl text-danger" aria-hidden />
        <p className="text-sm text-default-500 max-w-md">
          {!claimIntakeAiworkerId
            ? CLAIM_INTAKE_AGENT_NAME+ ' is not found in the agents.'
            : 'Unable to start claim intake. Please refresh and try again.'}
        </p>
        <CtyButton
          color="primary"
          variant="flat"
          size="sm"
          onPress={() => globalThis.location.reload()}
        >
          Retry
        </CtyButton>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-default-100 overflow-hidden relative">
      <div className="bg-content1 border-b border-default-200 px-3 sm:px-4 lg:px-6 py-2.5 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4 min-w-0">
          {intakeMode === 'new' ? (
            <StepperHeader currentStep={currentStep} completedSteps={completedSteps} />
          ) : (
            <div />
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4 min-w-0">
            {intakeMode === 'new' && isVoiceAgentAccessible ? (
              <ClaimIntakeVoiceFill
                token={authToken ?? null}
                formSetters={voiceFormSetters}
                currentStep={currentStep}
                onGoToStep={handleVoiceGoToStep}
                contractLookupRef={contractLookupRef}
                onRequestSubmitClaim={handleVoiceSubmitClaim}
              />
            ) : null}
            {aiworkerUsageId && (
              <ClaimIntakeHeaderFeedback messageId={aiworkerUsageId} />
            )}
          </div>
        </div>
      </div>

      <div ref={layoutRef} className="flex flex-1 overflow-hidden min-h-0">
        {pdfPreviewOpen ? (
          <ClaimIntakePdfPreviewPanel
            isOpen={pdfPreviewOpen}
            onClose={closePdfPreview}
            pdfFiles={pdfFiles}
            selectedFile={pdfPreviewSelectedFile}
            onSelectFile={setPdfPreviewSelectedFile}
            width={pdfPanelWidth ?? 400}
            onWidthChange={setPdfPanelWidth}
            layoutRef={layoutRef}
          />
        ) : null}

        {/* <div className="hidden lg:flex shrink-0 h-full" style={{ width: leftColW, minWidth: LEFT_W.min, maxWidth: LEFT_W.max }}>
          <ClaimLeftSidebar />
        </div> */}

        <hr
          aria-orientation="vertical"
          aria-label="Resize left panel"
          className={`hidden lg:block ${SEPARATOR_CLASS}`}
          onPointerDown={onColHandlePointerDown('left')}
          onPointerMove={onColHandlePointerMove}
          onPointerUp={onColHandlePointerUp}
          onPointerCancel={onColHandlePointerUp}
        />

        <div ref={mainScrollRef} className="flex-1 overflow-y-auto p-4 min-w-0">
          <div className="w-full max-w-none min-w-0">
            {showClaimScoreSidebar ? (
              <div className="lg:hidden mb-4">
                <ClaimScoreSidebar
                  currentStep={currentStep}
                  completedSteps={completedSteps}
                  compact
                  authToken={authToken}
                  contract={contract}
                  claimGenerationReview={claimGenerationReview}
                  claimScoreEnrichedForm={claimScoreEnrichedForm}
                  step3Repairs={step3Repairs}
                  dummyClaimId={dummyClaimId}
                  onDummyClaimIdChange={setDummyClaimId}
                  session={claimIntakeSession}
                  aiworkerUsageId={aiworkerUsageId}
                  runClaimScorePipeline={!isLgViewport}
                  onClaimScoreLoadingChange={setIsClaimScoreLoading}
                  pdfFiles={pdfFiles}
                  onViewPdf={openPdfPreview}
                />
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground">
                  {pageTitle}
                </h2>
                <p className="text-xs sm:text-sm text-default-500 mt-0.5">
                  {intakeMode === 'existing' &&
                    'Look up an existing claim, then upload supporting documents below.'}
                  {intakeMode === 'new' && currentStep === 0 &&
                    'Upload a repair document (optional) and search for an existing warranty contract by VIN or contract number to begin the claim process.'}
                  {intakeMode === 'new' && currentStep === 1 &&
                    'Confirm vehicle and coverage details for this contract.'}
                  {intakeMode === 'new' && currentStep === 2 && 'Review repair details with 3Cs and generate line items.'}
                  {intakeMode === 'new' && currentStep === 3 && 'Review all claim information before final submission.'}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {showStepCounter ? (
                  <>
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-primary text-white flex items-center justify-center text-[11px] sm:text-xs font-bold">
                      {currentStep + 1}
                    </div>
                    <span className="text-xs sm:text-sm text-default-500 whitespace-nowrap">
                      Step {currentStep + 1} of {STEPS.length}
                    </span>
                  </>
                ) : null}
              </div>
            </div>

            <div className={currentStep === 0 ? '' : 'hidden'} aria-hidden={currentStep !== 0}>
              {intakeMode === 'existing' ? (
                <ExistingClaimIntakePanel
                  phase={existingClaimPhase}
                  onPhaseChange={setExistingClaimPhase}
                  onBackToNewClaim={() => {
                    setIntakeMode('new');
                    setExistingClaimPhase('identify');
                  }}
                  onSubmitSuccess={() => {
                    setIntakeMode('new');
                    setExistingClaimPhase('identify');
                    setAiworkerUsageId(null);
                  }}
                  session={claimIntakeSession}
                  agentsBaseUrl={claimIntakeAgentsBaseUrl}
                  token={authToken ?? null}
                  lookupSourceId={lookupSourceId}
                  aiworkerUsageId={aiworkerUsageId}
                  onAiworkerUsageIdChange={(id) => {
                    if (!id?.trim()) return;
                    setAiworkerUsageId((prev) => prev ?? id.trim());
                  }}
                />
              ) : (
                <>
                  <ContractLookupTab
                    ref={contractLookupRef}
                    onContractFound={handleContractFound}
                    onLookupStart={handleLookupStart}
                    vin={lookupVin}
                    onVinChange={setLookupVin}
                    contractNumber={lookupContractNumber}
                    onContractNumberChange={setLookupContractNumber}
                    contract={contract}
                    session={claimIntakeSession}
                    agentsBaseUrl={claimIntakeAgentsBaseUrl}
                    token={authToken ?? null}
                    lookupSourceId={lookupSourceId}
                    setDummyClaimId={setDummyClaimId}
                    uploadedFiles={step3UploadedFiles}
                    onUploadedFilesChange={setStep3UploadedFiles}
                    documentFileSources={documentFileSources}
                    onDocumentFileSourcesChange={setDocumentFileSources}
                    parsedClaimDocument={step3ParsedDocument}
                    onParsedClaimDocumentChange={setStep3ParsedDocument}
                    onRepairStepArtifactsClear={clearStep3RepairArtifacts}
                    onClaimGenerationReset={resetClaimGeneration}
                    formSetters={contractLookupFormSetters}
                    onAiworkerUsageIdChange={(id) => {
                      if (!id?.trim()) return;
                      setAiworkerUsageId((prev) => prev ?? id.trim());
                    }}
                    aiworkerUsageId={aiworkerUsageId}
                    onViewPdf={openPdfPreview}
                    hideNoEstimatePrompt={hideNoEstimatePrompt}
                    onHideNoEstimatePrompt={() => setHideNoEstimatePrompt(true)}
                    isServicer={isServicer}
                    onIsServicerChange={setIsServicer}
                    onOpenExistingClaim={() => {
                      setIntakeMode('existing');
                      setExistingClaimPhase('identify');
                    }}
                    onPendingClaimNumbersChange={setPendingClaimNumbers}
                  />
                  {contract && isServicer === 'yes' ? (
                    <div className="mt-6 flex justify-end">
                      <CtyButton
                        color="primary"
                        size="lg"
                        isDisabled={hasBlockingPendingClaims(pendingClaimNumbers)}
                        onPress={() => setCurrentStep(1)}
                        endContent={<Icon icon="mdi:arrow-right" width={18} />}
                        className="font-semibold"
                      >
                        Continue To Vehicle & Claims
                      </CtyButton>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            {currentStep === 1 ? (
              <VehicleClaimsTab
                contract={contract}
                showPreviousClaims={showPreviousClaims}
                previousClaims={previousClaims}
                onPreviousClaimsChange={setPreviousClaims}
                previousClaimsFetchConfig={previousClaimsFetchConfig}
                onBack={() => setCurrentStep(0)}
                onContinue={() => {
                  setCompletedSteps((prev) => (prev.includes(1) ? prev : [...prev, 1]));
                  setCurrentStep(2);
                }}
              />
            ) : null}

            {currentStep === 2 ? (
              <RepairsDocsTab
                session={claimIntakeSession}
                agentsBaseUrl={claimIntakeAgentsBaseUrl}
                aiworkerUsageId={aiworkerUsageId ?? undefined}
                contract={contract}
                contractNumber={step3ContractNumber}
                dealerNumber={step3DealerNumber}
                onDealerNumberChange={setStep3DealerNumber}
                productType={step3ProductType}
                parsedClaimDocument={step3ParsedDocument}
                restoredClaimGeneration={claimGenerationReview}
                preparedClaimRequestKey={preparedClaimRequestKey}
                onPreparedClaimRequestKeyChange={setPreparedClaimRequestKey}
                arrivalMethod={arrivalMethod}
                onArrivalMethodChange={setArrivalMethod}
                atServiceCenter={atServiceCenter}
                onAtServiceCenterChange={setAtServiceCenter}
                reimbursement={step3Reimbursement}
                onReimbursementChange={setStep3Reimbursement}
                paymentEmail={step3PaymentEmail}
                onPaymentEmailChange={setStep3PaymentEmail}
                vehicleNotes={vehicleNotes}
                onVehicleNotesChange={handleVehicleNotesChange}
                additionalInfoFiles={additionalInfoFiles}
                onAdditionalInfoFilesChange={setAdditionalInfoFiles}
                additionalSources={additionalSources}
                onAdditionalSourcesChange={setAdditionalSources}
                documentFileSources={documentFileSources}
                servicerName={step3ServicerName}
                onServicerNameChange={setStep3ServicerName}
                servicerPhone={step3ServicerPhone}
                onServicerPhoneChange={setStep3ServicerPhone}
                servicerContactName={step3ServicerContactName}
                onServicerContactNameChange={setStep3ServicerContactName}
                servicerMobile={step3ServicerMobile}
                onServicerMobileChange={setStep3ServicerMobile}
                wantText={step3WantText}
                onWantTextChange={setStep3WantText}
                servicerAddress1={step3ServicerAddress1}
                onServicerAddress1Change={setStep3ServicerAddress1}
                servicerAddress2={step3ServicerAddress2}
                onServicerAddress2Change={setStep3ServicerAddress2}
                servicerCity={step3ServicerCity}
                onServicerCityChange={setStep3ServicerCity}
                servicerState={step3ServicerState}
                onServicerStateChange={setStep3ServicerState}
                servicerPostalCode={step3ServicerPostalCode}
                onServicerPostalCodeChange={setStep3ServicerPostalCode}
                servicerCountryCode={step3ServicerCountryCode}
                onServicerCountryCodeChange={setStep3ServicerCountryCode}
                servicerClassification={step3ServicerClassification}
                onServicerClassificationChange={setStep3ServicerClassification}
                contactEmail={step3ContactEmail}
                onContactEmailChange={setStep3ContactEmail}
                laborRate={step3LaborRate}
                onLaborRateChange={setStep3LaborRate}
                mileageIn={step3MileageIn}
                onMileageInChange={setStep3MileageIn}
                invoiceNumber={step3InvoiceNumber}
                onInvoiceNumberChange={setStep3InvoiceNumber}
                repairOrderDate={step3RepairOrderDate}
                onRepairOrderDateChange={setStep3RepairOrderDate}
                vehicleVin={step3VehicleVin}
                customerName={step3CustomerName}
                onCustomerNameChange={setStep3CustomerName}
                customerPhone={step3CustomerPhone}
                onCustomerPhoneChange={setStep3CustomerPhone}
                customerAddress1={step3CustomerAddress1}
                onCustomerAddress1Change={setStep3CustomerAddress1}
                customerAddress2={step3CustomerAddress2}
                onCustomerAddress2Change={setStep3CustomerAddress2}
                customerCity={step3CustomerCity}
                onCustomerCityChange={setStep3CustomerCity}
                customerState={step3CustomerState}
                onCustomerStateChange={setStep3CustomerState}
                customerPostalCode={step3CustomerPostalCode}
                onCustomerPostalCodeChange={setStep3CustomerPostalCode}
                customerCountryCode={step3CustomerCountryCode}
                onCustomerCountryCodeChange={setStep3CustomerCountryCode}
                repairs={step3Repairs}
                onRepairsChange={setStep3Repairs}
                token={authToken ?? null}
                paymentAuthorizationAcknowledged={paymentAuthorizationAcknowledged}
                setPaymentAuthorizationAcknowledged={setPaymentAuthorizationAcknowledged}
                onBack={() => setCurrentStep(1)}
                onClaimGenerationReviewChange={setClaimGenerationReview}
                onContinue={(data) => {
                  setClaimGenerationReview(data);
                  setCompletedSteps((prev) => (prev.includes(2) ? prev : [...prev, 2]));
                  setCurrentStep(3);
                }}
                onClaimScoreEnrichedForm={setClaimScoreEnrichedForm}
                servicerSourceId={servicerDetailsSourceId}
                dealerSourceId={dealerDetailsSourceId}
                onCustomerFromApiChange={setCustomerFromApi}
                onServicerFromApiChange={(data) => {
                  // Contact Email is manual entry only — do not apply servicer_info / 3csmapper email.
                  setStep3ServicerPhone(data.phone_no);
                  setStep3DealerNumber(data.dealer_no);
                  if (data.servicer_classification.trim()) {
                    setStep3ServicerClassification(normalizeServicerClassification(data.servicer_classification));
                  }
                  if (data.address) {
                    setStep3ServicerAddress1(data.address.address1);
                    setStep3ServicerAddress2(data.address.address2);
                    setStep3ServicerCity(data.address.city);
                    setStep3ServicerState(data.address.state);
                    setStep3ServicerPostalCode(data.address.postal_code);
                    setStep3ServicerCountryCode(data.address.country_code);
                  }
                }}
              />
            ) : null}

            {currentStep === 3 ? (
              <ReviewTab
                contract={contract}
                arrivalMethod={arrivalMethod}
                atServiceCenter={atServiceCenter}
                reimbursement={step3Reimbursement}
                paymentEmail={step3PaymentEmail}
                vehicleNotes={vehicleNotes}
                uploadedFiles={step3UploadedFiles}
                additionalInfoFiles={additionalInfoFiles}
                servicerName={step3ServicerName}
                servicerPhone={step3ServicerPhone}
                servicerContactName={step3ServicerContactName}
                servicerMobile={step3ServicerMobile}
                wantText={step3WantText}
                servicerAddress1={step3ServicerAddress1}
                servicerAddress2={step3ServicerAddress2}
                servicerCity={step3ServicerCity}
                servicerState={step3ServicerState}
                servicerPostalCode={step3ServicerPostalCode}
                servicerCountryCode={step3ServicerCountryCode}
                servicerClassification={step3ServicerClassification}
                contactEmail={step3ContactEmail}
                laborRate={step3LaborRate}
                mileageIn={step3MileageIn}
                invoiceNumber={step3InvoiceNumber}
                repairOrderDate={step3RepairOrderDate}
                vehicleVin={step3VehicleVin}
                customerName={step3CustomerName}
                customerPhone={step3CustomerPhone}
                customerAddress1={step3CustomerAddress1}
                customerAddress2={step3CustomerAddress2}
                customerCity={step3CustomerCity}
                customerState={step3CustomerState}
                customerPostalCode={step3CustomerPostalCode}
                customerCountryCode={step3CustomerCountryCode}
                contractNumber={step3ContractNumber}
                dealerNumber={step3DealerNumber}
                productType={step3ProductType}
                repairs={step3Repairs}
                claimGenerationReview={claimGenerationReview}
                onClaimObjectUpdated={handleClaimObjectUpdated}
                onBack={() => setCurrentStep(2)}
                session={claimIntakeSession}
                agentsBaseUrl={claimIntakeAgentsBaseUrl}
                authToken={authToken}
                submitSourceId={submitSourceId}
                claimScoreEnrichedForm={claimScoreEnrichedForm}
                isClaimScoreLoading={isClaimScoreLoading}
                waitForClaimScore={showClaimScoreSidebar}
                aiworkerUsageId={aiworkerUsageId ?? undefined}
                customerFromApi={customerFromApi}
                previousClaims={previousClaims}
                voiceSubmitNonce={voiceSubmitNonce}
                additionalSources={additionalSources}
                documentFileSources={documentFileSources}
              />
            ) : null}
          </div>
        </div>

        <hr
          aria-orientation="vertical"
          aria-label="Resize right panel"
          className={`hidden lg:block ${SEPARATOR_CLASS}`}
          onPointerDown={onColHandlePointerDown('right')}
          onPointerMove={onColHandlePointerMove}
          onPointerUp={onColHandlePointerUp}
          onPointerCancel={onColHandlePointerUp}
        />

        {showClaimScoreSidebar ? (
        <div
          className="hidden lg:flex shrink-0 h-full min-h-0 overflow-hidden"
          style={{ width: rightColW, minWidth: RIGHT_W.min, maxWidth: RIGHT_W.max }}
        >
          <ClaimScoreSidebar
            currentStep={currentStep}
            completedSteps={completedSteps}
            authToken={authToken}
            contract={contract}
            claimGenerationReview={claimGenerationReview}
            claimScoreEnrichedForm={claimScoreEnrichedForm}
            step3Repairs={step3Repairs}
            dummyClaimId={dummyClaimId}
            onDummyClaimIdChange={setDummyClaimId}
            session={claimIntakeSession}
            runClaimScorePipeline={isLgViewport}
            onClaimScoreLoadingChange={setIsClaimScoreLoading}
            pdfFiles={pdfFiles}
            onViewPdf={openPdfPreview}
            aiworkerUsageId={aiworkerUsageId ?? undefined}
          />
        </div>
        ) : null}
      </div>

      {showBootstrapLoader ? (
        <div className="absolute inset-0 z-30 bg-background/70 backdrop-blur-[1px] flex items-center justify-center px-4">
          {/* <div className="flex flex-col items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm"> */}
            <CtySpinner size="lg" />
            <p className="text-sm font-medium text-default-700 pl-4">{'Setting up claim intake form'}</p>
          {/* </div> */}
        </div>
      ) : null}
    </div>
  );
}
